import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import RaisedButton from 'material-ui/RaisedButton';
import { Tooltip } from '@material-ui/core';
import {
  fetchInspections,
  findDescriptorByKey,
  isSortableDescriptor,
  fetchInspectionHistory,
  fetchInspectionScheduled,
  setInspectSelection,
  setInspectPivot,
  clearInspectionRecords,
  setInspectColumnFilter,
  clearInspectColumnFilters,
  setInspectSort,
  setInspectColumns,
  setInspectDateRange,
  setInspectScheduledColumnFilter,
  clearInspectScheduledColumnFilters,
  setInspectScheduledSort,
  setInspectScheduledColumns,
  scheduledDateBounds,
  historyDateBounds,
  fetchInspectionPerformance,
  isSameDateRange,
  postDeficiencyAction,
  fetchInspectionRecordActivity,
  fetchInspectionVoidPreview,
  postInspectionVoid,
  cancelInspectReindexRefresh,
} from 'store/actions/inspect';
import { openSlateInPane } from 'store/actions/slatePane';
import { entityNames, route, slateTypes, INSPECT_STATUS_UPCOMING } from 'config/constants';
import InspectDropdown from './InspectDropdown';
import InspectRecords, { PASSED_RESULT_VALUES } from './InspectRecords';
import InspectPerformance from './InspectPerformance';
import { buildFilterChips } from './filterChips';
import InspectDateRange from './InspectDateRange/InspectDateRange';
import DelayedLoad from 'components/Shared/DelayedLoad/DelayedLoad';
import EmptyState from 'components/Shared/EmptyState/EmptyState';
import palette from '../../theme/palette';
import './style.css';

var TABS = {
  HISTORY: 'history',
  SCHEDULED: 'scheduled',
  PERFORMANCE: 'performance',
  REPORTS: 'reports',
};

var SS_COLUMN_FILTERS_KEY = 'inspectColumnFilters';
var SS_COLUMN_SORT_KEY = 'inspectColumnSort';
var SS_COLUMN_DESCRIPTORS_KEY = 'inspectColumnDescriptors';
var SS_ACTIVE_TAB_KEY = 'inspectActiveTab';
var SS_PIVOT_KEY = 'inspectPivot';

// Bump whenever a persisted History OR Scheduled descriptor set could carry a
// stale filterKey binding the API no longer honours -- this release rebinds
// History's timestamp/user columns from metadata (LastChangedTimestamp/User)
// to the renamed reserved fields (__inspect_completedAt/__inspect_completedByName),
// and separately renames the Scheduled template's due-date field
// (nextDueDate -> __inspect_nextDueDate, a new pathHash) and adds
// __inspect_lastInspectedAt, so descriptor sets cached from before it must
// not be trusted on either tab. Bumping this version, not the column KEYS
// (INSPECT_STABLE_KEYS / INSPECT_SCHEDULED_STABLE_KEYS are unchanged), is
// what makes readDescriptorsFromSession / readScheduledDescriptorsFromSession
// discard the stale sets below.
// Version 3 adds the persisted custom column sets (custom columns stage 4), so
// sets cached by older code are not mixed with them.
var INSPECT_DESCRIPTOR_SET_VERSION = 3;
var SS_COLUMN_DESCRIPTORS_VERSION_KEY = 'inspectColumnDescriptorsVersion';
var SS_SCHEDULED_COLUMN_DESCRIPTORS_VERSION_KEY =
  'inspectScheduledColumnDescriptorsVersion';
var SS_CUSTOM_COLUMNS_KEY = 'inspectCustomColumns';
var SS_SCHEDULED_CUSTOM_COLUMNS_KEY = 'inspectScheduledCustomColumns';

var INSPECT_STABLE_KEYS = [
  'asset-name',
  'inspection',
  'timestamp',
  'user',
  'result',
];

var SS_SCHEDULED_COLUMN_FILTERS_KEY = 'inspectScheduledColumnFilters';
var SS_SCHEDULED_COLUMN_SORT_KEY = 'inspectScheduledColumnSort';
var SS_SCHEDULED_COLUMN_DESCRIPTORS_KEY = 'inspectScheduledColumnDescriptors';

var INSPECT_SCHEDULED_STABLE_KEYS = [
  'asset-name',
  'inspection',
  'due-date',
  'status',
  'frequency',
  'last-inspected',
];

// The custom column set persisted beside a tab's descriptor set, under that
// tab's descriptor version key. Discarded (and removed) when the version does
// not match, so it is never trusted across a descriptor-set bump.
function readCustomSetFromSession(key, versionKey) {
  try {
    if (sessionStorage.getItem(versionKey) !== String(INSPECT_DESCRIPTOR_SET_VERSION)) {
      sessionStorage.removeItem(key);
      return null;
    }
    var raw = sessionStorage.getItem(key);
    if (!raw) return null;
    var parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : null;
  } catch (e) {
    return null;
  }
}

export function readCustomColumnsFromSession() {
  return readCustomSetFromSession(SS_CUSTOM_COLUMNS_KEY, SS_COLUMN_DESCRIPTORS_VERSION_KEY);
}

export function readScheduledCustomColumnsFromSession() {
  return readCustomSetFromSession(SS_SCHEDULED_CUSTOM_COLUMNS_KEY, SS_SCHEDULED_COLUMN_DESCRIPTORS_VERSION_KEY);
}

function readFiltersFromSession() {
  try {
    var raw = sessionStorage.getItem(SS_COLUMN_FILTERS_KEY);
    if (!raw) return null;
    var parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') {
      var filtered = {};
      var keys = Object.keys(parsed);
      for (var i = 0; i < keys.length; i++) {
        if (INSPECT_STABLE_KEYS.indexOf(keys[i]) !== -1) {
          filtered[keys[i]] = parsed[keys[i]];
        }
      }
      return filtered;
    }
    return null;
  } catch (e) {
    return null;
  }
}

// No descriptor-version check needed here: a persisted sort is only a
// { columnKey, direction } pair validated against INSPECT_STABLE_KEYS, which
// is unchanged, and History's date-sort inversion keys on a fixed column
// index rather than a descriptor's filterKey. It rebinds to whichever fresh
// descriptor the column key now resolves to without needing to know that
// descriptor's shape (unlike readScheduledSortFromSession below, which does
// consult sortability off the descriptor and is therefore already
// version-agnostic the other way -- restored descriptors it is handed
// either carry the flag or restore no sort at all).
function readSortFromSession() {
  try {
    var raw = sessionStorage.getItem(SS_COLUMN_SORT_KEY);
    if (!raw) return null;
    var parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    if (!parsed.columnKey || !parsed.direction) return null;
    if (INSPECT_STABLE_KEYS.indexOf(parsed.columnKey) === -1) return null;
    return parsed;
  } catch (e) {
    return null;
  }
}

function readActiveTabFromSession() {
  try {
    var raw = sessionStorage.getItem(SS_ACTIVE_TAB_KEY);
    if (!raw) return null;
    if (
      raw === TABS.HISTORY ||
      raw === TABS.SCHEDULED ||
      raw === TABS.PERFORMANCE ||
      raw === TABS.REPORTS
    ) {
      return raw;
    }
    return null;
  } catch (e) {
    return null;
  }
}

// The active dropdown pivot is a view preference (not reflected in the URL),
// persisted across navigation like the other Inspect sessionStorage keys.
function readPivotFromSession() {
  try {
    var raw = sessionStorage.getItem(SS_PIVOT_KEY);
    if (raw === 'byInspection' || raw === 'byAssetType') return raw;
    return null;
  } catch (e) {
    return null;
  }
}

// Exported for testing. A descriptor set persisted under an older
// INSPECT_DESCRIPTOR_SET_VERSION (including no version at all, the real
// upgrade path -- an existing session predates the key) is discarded
// outright rather than trusted, so it is never handed to setInspectColumns.
// The user's filters and sort are read separately, by their stable UI key
// (readFiltersFromSession / readSortFromSession below), and are NOT
// discarded here -- they rebind to the fresh descriptors the first History
// response delivers, through the existing appliedColumnFilterKeys /
// historyFetchKey mechanism (a filter set before descriptors are known
// already refetches once they arrive and the filter becomes applicable).
export function readDescriptorsFromSession() {
  try {
    var storedVersion = sessionStorage.getItem(
      SS_COLUMN_DESCRIPTORS_VERSION_KEY,
    );
    if (storedVersion !== String(INSPECT_DESCRIPTOR_SET_VERSION)) {
      sessionStorage.removeItem(SS_COLUMN_DESCRIPTORS_KEY);
      sessionStorage.removeItem(SS_COLUMN_DESCRIPTORS_VERSION_KEY);
      return null;
    }
    var raw = sessionStorage.getItem(SS_COLUMN_DESCRIPTORS_KEY);
    if (!raw) return null;
    var parsed = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    return null;
  } catch (e) {
    return null;
  }
}

function readScheduledFiltersFromSession() {
  try {
    var raw = sessionStorage.getItem(SS_SCHEDULED_COLUMN_FILTERS_KEY);
    if (!raw) return null;
    var parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') {
      var filtered = {};
      var keys = Object.keys(parsed);
      for (var i = 0; i < keys.length; i++) {
        if (INSPECT_SCHEDULED_STABLE_KEYS.indexOf(keys[i]) !== -1) {
          filtered[keys[i]] = parsed[keys[i]];
        }
      }
      return filtered;
    }
    return null;
  } catch (e) {
    return null;
  }
}

// Exported for testing: a sort persisted from a session before a column
// became unsortable (e.g. Frequency, a computed phrase) must not be restored
// on mount, or it renders no header indicator and can never be cleared from
// the UI. Sortability is read off the descriptors persisted alongside the
// sort (isSortableDescriptor), so descriptors from before the API carried
// the flag, or none at all, restore no sort. Checked independently of
// INSPECT_SCHEDULED_STABLE_KEYS, which tracks known column keys for
// filter/sort restore generally, not sortability.
export function readScheduledSortFromSession(descriptors) {
  try {
    var raw = sessionStorage.getItem(SS_SCHEDULED_COLUMN_SORT_KEY);
    if (!raw) return null;
    var parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    if (!parsed.columnKey || !parsed.direction) return null;
    if (INSPECT_SCHEDULED_STABLE_KEYS.indexOf(parsed.columnKey) === -1)
      return null;
    if (
      !isSortableDescriptor(findDescriptorByKey(descriptors, parsed.columnKey))
    )
      return null;
    return parsed;
  } catch (e) {
    return null;
  }
}

// Exported for testing. Mirrors readDescriptorsFromSession's version gate
// (see INSPECT_DESCRIPTOR_SET_VERSION above): a descriptor set persisted
// under an older version, or with no version key at all, is discarded
// outright rather than trusted with a stale filterKey.
export function readScheduledDescriptorsFromSession() {
  try {
    var storedVersion = sessionStorage.getItem(
      SS_SCHEDULED_COLUMN_DESCRIPTORS_VERSION_KEY,
    );
    if (storedVersion !== String(INSPECT_DESCRIPTOR_SET_VERSION)) {
      sessionStorage.removeItem(SS_SCHEDULED_COLUMN_DESCRIPTORS_KEY);
      sessionStorage.removeItem(SS_SCHEDULED_COLUMN_DESCRIPTORS_VERSION_KEY);
      return null;
    }
    var raw = sessionStorage.getItem(SS_SCHEDULED_COLUMN_DESCRIPTORS_KEY);
    if (!raw) return null;
    var parsed = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    return null;
  } catch (e) {
    return null;
  }
}

// Derive selection from the matched route + path params.
// Returns null when no selection (bare /inspect default path).
function parseSelectionFromMatch(match, routeConstants) {
  if (!match) return null;
  if (match.path === routeConstants.INSPECT_ALL) {
    return { mode: 'all', value: null };
  }
  if (match.path === routeConstants.INSPECT_ASSET_TYPE) {
    var hash = match.params.assetTypeHash;
    if (!hash) return null;
    var valueParam = match.params.assetTypeValue;
    var value = valueParam ? decodeURIComponent(valueParam) : '';
    return { mode: 'assetType', value: { hash: hash, value: value } };
  }
  if (match.path === routeConstants.INSPECT_SELECTION_TYPE) {
    var typeUUID = match.params.inspectionUUID;
    if (!typeUUID) return null;
    var atValueParam = match.params.assetTypeValue;
    var atValue = atValueParam ? decodeURIComponent(atValueParam) : '';
    return {
      mode: 'inspectionType',
      value: { inspectionUUID: typeUUID, assetTypeValue: atValue },
    };
  }
  if (match.path === routeConstants.INSPECT_SELECTION) {
    var uuid = match.params.inspectionUUID;
    if (!uuid) return null;
    return { mode: 'inspection', value: uuid };
  }
  return null;
}

function buildSelectionPath(accountUUID, mode, value) {
  var base = '/accounts/' + (accountUUID || '') + '/inspect';
  if (mode === 'inspection' && value) return base + '/' + value;
  if (mode === 'inspectionType' && value && value.inspectionUUID) {
    return (
      base +
      '/' +
      value.inspectionUUID +
      '/at/' +
      encodeURIComponent(value.assetTypeValue || '')
    );
  }
  if (mode === 'all') return base + '/all';
  if (mode === 'assetType' && value && value.hash) {
    return (
      base + '/at/' + value.hash + '/' + encodeURIComponent(value.value || '')
    );
  }
  return base;
}

// True if the byAssetType pivot contains an assetTypeNode matching the given
// field path hash (and value, when provided).
function hasAssetTypeNode(byAssetType, hash, value) {
  if (!hash) return false;
  var items = byAssetType || [];
  for (var i = 0; i < items.length; i++) {
    var item = items[i];
    if (item.kind === 'assetTypeNode' && item.assetTypeFieldPathHash === hash) {
      if (value === undefined || value === null) return true;
      if (item.assetTypeValue === value) return true;
    }
  }
  return false;
}

// True if any leaf inspection across either pivot has the given uuid.
// UUID of the single inspection when exactly one exists, else null.
function soleInspectionUUID(byInspection, byAssetType) {
  var lists = [byInspection || [], byAssetType || []];
  for (var l = 0; l < lists.length; l++) {
    var items = lists[l];
    if (countInspections(items) !== 1) continue;
    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      if (item.kind === 'inspection') return item.inspectionUUID;
      if (
        item.kind === 'assetTypeNode' &&
        item.inspections &&
        item.inspections.length
      )
        return item.inspections[0].inspectionUUID;
    }
  }
  return null;
}

function hasInspectionUUID(byInspection, byAssetType, uuid) {
  if (!uuid) return false;
  var lists = [byInspection || [], byAssetType || []];
  for (var l = 0; l < lists.length; l++) {
    var items = lists[l];
    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      if (item.kind === 'inspection' && item.inspectionUUID === uuid)
        return true;
      if (item.kind === 'assetTypeNode' && item.inspections) {
        for (var j = 0; j < item.inspections.length; j++) {
          if (item.inspections[j].inspectionUUID === uuid) return true;
        }
      }
    }
  }
  return false;
}

// True if the given inspection is scoped-able to the given asset-type value:
// either the byInspection item advertises that asset type, or a byAssetType
// group for that value nests the inspection.
function hasInspectionType(byInspection, byAssetType, uuid, assetTypeValue) {
  if (!uuid) return false;
  var byI = byInspection || [];
  for (var i = 0; i < byI.length; i++) {
    var item = byI[i];
    if (
      item.kind === 'inspection' &&
      item.inspectionUUID === uuid &&
      item.assetTypes
    ) {
      for (var j = 0; j < item.assetTypes.length; j++) {
        if (item.assetTypes[j].assetTypeValue === assetTypeValue) return true;
      }
    }
  }
  var byA = byAssetType || [];
  for (var a = 0; a < byA.length; a++) {
    var node = byA[a];
    if (
      node.kind === 'assetTypeNode' &&
      node.assetTypeValue === assetTypeValue &&
      node.inspections
    ) {
      for (var k = 0; k < node.inspections.length; k++) {
        if (node.inspections[k].inspectionUUID === uuid) return true;
      }
    }
  }
  return false;
}

// Display an asset-type value prefixed with its field name for context, e.g.
// "Type: Foam". Falls back to the bare value when the field name is missing.
function assetTypeDisplay(fieldName, value) {
  var v = value !== null && value !== undefined ? value : '';
  return fieldName ? fieldName + ': ' + v : v;
}

// The asset-type field name for the current selection. selectionValue carries
// only hash/value (assetType) or inspectionUUID/assetTypeValue (inspectionType),
// so the field name is looked up from the pivot data. Returns '' when unknown.
function assetTypeFieldNameForSelection(props) {
  var mode = props.selectionMode;
  var value = props.selectionValue;
  if (mode === 'assetType' && value && value.hash) {
    var byA = props.inspectionsByAssetType || [];
    for (var i = 0; i < byA.length; i++) {
      var node = byA[i];
      if (
        node.kind === 'assetTypeNode' &&
        node.assetTypeFieldPathHash === value.hash &&
        node.assetTypeValue === value.value
      ) {
        return node.assetTypeFieldName || '';
      }
    }
  } else if (mode === 'inspectionType' && value && value.inspectionUUID) {
    var byI = props.inspectionsByInspection || [];
    for (var j = 0; j < byI.length; j++) {
      var it = byI[j];
      if (
        it.kind === 'inspection' &&
        it.inspectionUUID === value.inspectionUUID
      ) {
        return it.assetTypeFieldName || '';
      }
    }
  }
  return '';
}

// Returns a stable string key representing the current selection, or null when
// there is no usable selection yet (which gates the per-tab fetch). Client-side
// fetch-dedup identity; distinct per mode/value.
// - inspection     -> 'inspection:' + uuid                    (null if no uuid)
// - inspectionType -> 'inspectionType:' + uuid + ':' + value  (null if no uuid)
// - assetType      -> 'assetType:' + hash + '/' + value       (null if no hash)
// - all            -> 'all'
function selectionKey(props) {
  if (props.selectionMode === 'inspection') {
    var uuid = props.currentInspection && props.currentInspection.uuid;
    if (!uuid)
      uuid =
        typeof props.selectionValue === 'string' ? props.selectionValue : null;
    if (!uuid) return null;
    return 'inspection:' + uuid;
  }
  if (props.selectionMode === 'inspectionType') {
    var value = props.selectionValue || {};
    var typeUUID =
      (props.currentInspection && props.currentInspection.uuid) ||
      value.inspectionUUID;
    if (!typeUUID) return null;
    var atValue =
      value.assetTypeValue !== null && value.assetTypeValue !== undefined
        ? value.assetTypeValue
        : '';
    return 'inspectionType:' + typeUUID + ':' + atValue;
  }
  if (props.selectionMode === 'assetType') {
    var selectionValue = props.selectionValue;
    if (selectionValue && selectionValue.hash) {
      return (
        'assetType:' + selectionValue.hash + '/' + String(selectionValue.value)
      );
    }
    return null;
  }
  if (props.selectionMode === 'all') {
    return 'all';
  }
  return null;
}

// Whether a (mode, value) selection coming from the dropdown matches the
// selection already active. Used to no-op re-selecting the same item: otherwise
// setInspectSelection clears the loaded records without changing any fetch key,
// so _maybeFetch* skips the refetch and the tab goes blank until a manual
// refresh.
function isSameSelection(mode, value, currentMode, currentValue) {
  if (mode !== currentMode) return false;
  if (mode === 'inspection') return value === currentValue;
  if (mode === 'inspectionType') {
    var av = value || {};
    var bv = currentValue || {};
    return (
      av.inspectionUUID === bv.inspectionUUID &&
      av.assetTypeValue === bv.assetTypeValue
    );
  }
  if (mode === 'assetType') {
    var a = value || {};
    var b = currentValue || {};
    return a.hash === b.hash && a.value === b.value;
  }
  // 'all' (or any other valueless mode): matching mode is enough.
  return true;
}

// The date range is shared across tabs but each tab only uses one side of it:
// History/Performance read the PAST side, Scheduled reads the FUTURE side. A
// custom from/to overrides both sides, so it appears in both keys. These use the
// raw range descriptor (not computed bounds) so the key stays stable across
// renders -- computed preset bounds drift by the millisecond and would loop.
function pastRangeKey(dateRange) {
  var dr = dateRange || {};
  return (
    (dr.past || '') + '|' + (dr.customFrom || '') + '|' + (dr.customTo || '')
  );
}

function futureRangeKey(dateRange) {
  var dr = dateRange || {};
  return (
    (dr.future || '') + '|' + (dr.customFrom || '') + '|' + (dr.customTo || '')
  );
}

// The account half of each fetch key. The thunks silently no-op without a
// loaded account, but _maybeFetch* burns its dedup key BEFORE dispatching --
// so a key that resolves while the post-refresh account restore is still in
// flight would swallow the tab's only fetch and never retry (the key does not
// change when the account arrives). Returning null keeps the guard unburned
// until a request can actually be sent; baking the uuid in also refetches
// when the account changes.
function accountKey(props) {
  var account = props.currentAccount;
  return account && account.uuid ? account.uuid : null;
}

// Fetch identity for each tab: the account and selection plus every other
// input that tab's query depends on. Because the relevant date side is baked
// in, changing the date range (or this tab's column filters / sort) changes
// the key, so the _maybeFetch* guard refetches even when the change was made
// while a different tab was active -- not just on the tab that happened to be
// open at the time.
// Performance scopes to BOTH date sides. The past side drives the loaded
// inspection metrics (Total / Passed / Failed) today; the future side is
// included ahead of the forward-looking metrics (Overdue / Upcoming) so a
// future-range change already reloads the tab. Keep in sync with
// performanceSignature / buildPerformanceQuery in store/actions/inspect.js.
// Exported for unit testing.
export function performanceFetchKey(props) {
  var account = accountKey(props);
  var sel = selectionKey(props);
  if (!account || !sel) return null;
  return (
    account +
    '|' +
    sel +
    '|' +
    pastRangeKey(props.dateRange) +
    '|' +
    futureRangeKey(props.dateRange)
  );
}

// Sorted list of active column-filter keys that currently resolve to a loaded
// descriptor carrying a filterKey -- i.e. the filters that can actually be sent
// to the API right now. Folded into historyFetchKey so that a filter set before
// the column descriptors are known (e.g. a Performance KPI card click on a cold
// session, where recordsColumns is still null) refetches once the descriptors
// arrive from the first History response and the filter becomes applicable.
function appliedColumnFilterKeys(columnFilters, descriptors) {
  if (!columnFilters || !descriptors) return '';
  var applied = [];
  var keys = Object.keys(columnFilters);
  for (var i = 0; i < keys.length; i++) {
    var key = keys[i];
    var value = columnFilters[key];
    if (value === '' || value === null || value === undefined) continue;
    for (var j = 0; j < descriptors.length; j++) {
      if (descriptors[j].key === key && descriptors[j].filterKey) {
        applied.push(key);
        break;
      }
    }
  }
  applied.sort();
  return applied.join(',');
}

// Whether the current History sort resolves against the loaded descriptors --
// folded into historyFetchKey below for the same reason appliedColumnFilterKeys
// is: a sort set (or restored from sessionStorage) before recordsColumns is
// known -- including the window right after a stale, version-mismatched
// descriptor set is discarded on mount -- must refetch once fresh descriptors
// arrive and either resolve it or confirm it stays unresolvable. Without this,
// the raw sortColumnKey/sortDirection strings are unchanged across that
// transition, historyFetchKey does not change, and no refetch happens even
// though the first request (sent with no descriptors) omitted Query-Sort
// entirely -- leaving the sort indicator shown over unsorted results.
function appliedSortDescriptorKey(sortColumnKey, descriptors) {
  if (!sortColumnKey) return '';
  return isSortableDescriptor(findDescriptorByKey(descriptors, sortColumnKey))
    ? 'sortable'
    : 'unsortable';
}

// Exported for unit testing.
export function historyFetchKey(props) {
  var account = accountKey(props);
  var sel = selectionKey(props);
  if (!account || !sel) return null;
  return (
    account +
    '|' +
    sel +
    '|' +
    JSON.stringify(props.columnFilters || {}) +
    '|' +
    appliedColumnFilterKeys(props.columnFilters, props.recordsColumns) +
    '|' +
    (props.sortColumnKey || '') +
    '|' +
    (props.sortDirection || '') +
    '|' +
    appliedSortDescriptorKey(props.sortColumnKey, props.recordsColumns) +
    '|' +
    pastRangeKey(props.dateRange)
  );
}

// Exported for unit testing.
export function scheduledFetchKey(props) {
  var account = accountKey(props);
  var sel = selectionKey(props);
  if (!account || !sel) return null;
  return (
    account +
    '|' +
    sel +
    '|' +
    JSON.stringify(props.scheduledColumnFilters || {}) +
    '|' +
    // Same reason historyFetchKey folds in appliedColumnFilterKeys: a filter
    // restored from sessionStorage before scheduledColumns is known (a cold
    // mount, or the window right after readScheduledDescriptorsFromSession
    // discards a stale, version-mismatched cache) resolves to nothing in
    // buildScheduledFilterDTO's descriptors == [] case and is silently
    // dropped from the first request. Without this term the key is
    // unchanged when the real descriptors arrive, so nothing refetches to
    // pick the filter back up even though its chip is still shown as active.
    appliedColumnFilterKeys(props.scheduledColumnFilters, props.scheduledColumns) +
    '|' +
    (props.scheduledSortColumnKey || '') +
    '|' +
    (props.scheduledSortDirection || '') +
    '|' +
    futureRangeKey(props.dateRange)
  );
}

// Total leaf-inspection count across a single pivot list.
function countInspections(items) {
  var list = items || [];
  var n = 0;
  for (var i = 0; i < list.length; i++) {
    var item = list[i];
    if (item.kind === 'inspection') n += 1;
    else if (item.kind === 'assetTypeNode' && item.inspections)
      n += item.inspections.length;
  }
  return n;
}

// Canonical inspection count across both pivots. byInspection is one row per
// inspection, so it is preferred; fall back to byAssetType if it is empty.
function totalInspections(props) {
  if (!props) return 0;
  var byInspection = countInspections(props.inspectionsByInspection || []);
  if (byInspection > 0) return byInspection;
  return countInspections(props.inspectionsByAssetType || []);
}

// Exported (in addition to the default withTranslation/withRouter/connect-wrapped
// component below) for unit testing lifecycle methods directly.
export class Inspect extends Component {
  constructor(props) {
    super(props);
    var restoredTab = readActiveTabFromSession();
    this.state = {
      activeTab: restoredTab || TABS.PERFORMANCE,
      isDropdownOpen: false,
      dropdownAnchorEl: null,
    };
    this._urlSelectionApplied = false;
    this._lastFetchedHistoryKey = null;
    this._lastFetchedScheduledKey = null;
    this._lastFetchedPerformanceKey = null;
    // True while a load-more (pagination) fetch is the in-flight request, so
    // render can tell pagination (append skeleton rows) from a full reload
    // (gated through DelayedLoad). Set at each fetch's dispatch site; only read
    // while that tab is fetching, so it needs no reset on completion.
    this._historyLoadMore = false;
    this._scheduledLoadMore = false;
  }

  componentDidMount() {
    var restoredDescriptors = readDescriptorsFromSession();
    if (restoredDescriptors) {
      this.props.setInspectColumns(
        restoredDescriptors,
        readCustomColumnsFromSession() || undefined,
      );
    }

    var restoredFilters = readFiltersFromSession();
    if (restoredFilters) {
      var keys = Object.keys(restoredFilters);
      for (var i = 0; i < keys.length; i++) {
        this.props.setInspectColumnFilter(keys[i], restoredFilters[keys[i]]);
      }
    }
    var restoredSort = readSortFromSession();
    if (restoredSort) {
      this.props.setInspectSort(restoredSort.columnKey, restoredSort.direction);
    }

    var restoredPivot = readPivotFromSession();
    if (restoredPivot) {
      this.props.setInspectPivot(restoredPivot);
    }

    var currentAccount = this.props.currentAccount;
    var inspectionsLoaded = totalInspections(this.props) > 0;

    if (inspectionsLoaded) {
      this._tryRestoreUrlSelection();
    }

    if (currentAccount && currentAccount.uuid && !inspectionsLoaded) {
      this.props.fetchInspections(currentAccount.uuid);
    }

    var restoredScheduledDescriptors = readScheduledDescriptorsFromSession();
    if (restoredScheduledDescriptors) {
      this.props.setInspectScheduledColumns(
        restoredScheduledDescriptors,
        readScheduledCustomColumnsFromSession() || undefined,
      );
    }

    var restoredScheduledFilters = readScheduledFiltersFromSession();
    if (restoredScheduledFilters) {
      var skeys = Object.keys(restoredScheduledFilters);
      for (var si = 0; si < skeys.length; si++) {
        this.props.setInspectScheduledColumnFilter(
          skeys[si],
          restoredScheduledFilters[skeys[si]],
        );
      }
    }
    var restoredScheduledSort = readScheduledSortFromSession(
      restoredScheduledDescriptors,
    );
    if (restoredScheduledSort) {
      this.props.setInspectScheduledSort(
        restoredScheduledSort.columnKey,
        restoredScheduledSort.direction,
      );
    }

    this._maybeFetchHistory();
    this._maybeFetchScheduled();
    this._maybeFetchPerformance();
  }

  componentDidUpdate(prevProps) {
    var currentAccount = this.props.currentAccount;
    var prevAccount = prevProps.currentAccount;

    // Account changed
    if (
      currentAccount &&
      (!prevAccount || prevAccount.uuid !== currentAccount.uuid)
    ) {
      // A reindex refresh scheduled for the outgoing account would no-op at
      // fire time anyway (same-account check in scheduleInspectReindexRefresh),
      // but cancel it outright rather than leaving it to fire and bail.
      this.props.cancelInspectReindexRefresh();
      this._urlSelectionApplied = false;
      this.props.fetchInspections(currentAccount.uuid);
      return;
    }

    // Inspections just loaded -- restore URL selection. The selection-change branch
    // below will fire the actual fetch on the next update.
    var prevCount = totalInspections(prevProps);
    var nextCount = totalInspections(this.props);
    if (nextCount > 0 && prevCount === 0) {
      var didDispatchSelection = this._tryRestoreUrlSelection();
      if (didDispatchSelection) return;
    }

    var filtersChanged =
      JSON.stringify(prevProps.columnFilters) !==
      JSON.stringify(this.props.columnFilters);
    var sortChanged =
      prevProps.sortColumnKey !== this.props.sortColumnKey ||
      prevProps.sortDirection !== this.props.sortDirection;

    var scheduledFiltersChanged =
      JSON.stringify(prevProps.scheduledColumnFilters) !==
      JSON.stringify(this.props.scheduledColumnFilters);
    var scheduledSortChanged =
      prevProps.scheduledSortColumnKey !== this.props.scheduledSortColumnKey ||
      prevProps.scheduledSortDirection !== this.props.scheduledSortDirection;

    // No per-tab refetch special-cases here. Each tab's _maybeFetch* (called at
    // the end of this method, plus on mount and on tab switch) keys on every
    // input its query depends on -- selection, that tab's column filters/sort,
    // and the relevant side of the shared date range. So changing the date range
    // (or any other input) invalidates every affected tab's cache, and each tab
    // refetches when it next becomes active, not only the tab that was open when
    // the change was made. The remaining *Changed flags below drive sessionStorage
    // persistence only.

    if (filtersChanged) {
      try {
        sessionStorage.setItem(
          SS_COLUMN_FILTERS_KEY,
          JSON.stringify(this.props.columnFilters || {}),
        );
      } catch (e) {
        /* sessionStorage unavailable */
      }
    }
    if (sortChanged) {
      try {
        var sortPayload = this.props.sortColumnKey
          ? {
              columnKey: this.props.sortColumnKey,
              direction: this.props.sortDirection,
            }
          : null;
        if (sortPayload) {
          sessionStorage.setItem(
            SS_COLUMN_SORT_KEY,
            JSON.stringify(sortPayload),
          );
        } else {
          sessionStorage.removeItem(SS_COLUMN_SORT_KEY);
        }
      } catch (e) {
        /* sessionStorage unavailable */
      }
    }

    var descriptorsChanged =
      prevProps.recordsColumns !== this.props.recordsColumns;
    if (descriptorsChanged) {
      try {
        if (this.props.recordsColumns && this.props.recordsColumns.length > 0) {
          sessionStorage.setItem(
            SS_COLUMN_DESCRIPTORS_KEY,
            JSON.stringify(this.props.recordsColumns),
          );
          sessionStorage.setItem(
            SS_COLUMN_DESCRIPTORS_VERSION_KEY,
            String(INSPECT_DESCRIPTOR_SET_VERSION),
          );
        } else {
          sessionStorage.removeItem(SS_COLUMN_DESCRIPTORS_KEY);
          sessionStorage.removeItem(SS_COLUMN_DESCRIPTORS_VERSION_KEY);
        }
      } catch (e) {
        /* sessionStorage unavailable */
      }
    }

    if (scheduledFiltersChanged) {
      try {
        sessionStorage.setItem(
          SS_SCHEDULED_COLUMN_FILTERS_KEY,
          JSON.stringify(this.props.scheduledColumnFilters || {}),
        );
      } catch (e) {
        /* sessionStorage unavailable */
      }
    }
    if (scheduledSortChanged) {
      try {
        var scheduledSortPayload = this.props.scheduledSortColumnKey
          ? {
              columnKey: this.props.scheduledSortColumnKey,
              direction: this.props.scheduledSortDirection,
            }
          : null;
        if (scheduledSortPayload) {
          sessionStorage.setItem(
            SS_SCHEDULED_COLUMN_SORT_KEY,
            JSON.stringify(scheduledSortPayload),
          );
        } else {
          sessionStorage.removeItem(SS_SCHEDULED_COLUMN_SORT_KEY);
        }
      } catch (e) {
        /* sessionStorage unavailable */
      }
    }

    [
      ['recordsCustomColumns', SS_CUSTOM_COLUMNS_KEY],
      ['scheduledCustomColumns', SS_SCHEDULED_CUSTOM_COLUMNS_KEY],
    ].forEach(function (pair) {
      var propName = pair[0];
      var ssKey = pair[1];
      if (prevProps[propName] === this.props[propName]) return;
      try {
        if (Array.isArray(this.props[propName]) && this.props[propName].length > 0) {
          sessionStorage.setItem(ssKey, JSON.stringify(this.props[propName]));
        } else {
          sessionStorage.removeItem(ssKey);
        }
      } catch (e) {
        /* sessionStorage unavailable */
      }
    }, this);

    var scheduledDescriptorsChanged =
      prevProps.scheduledColumns !== this.props.scheduledColumns;
    if (scheduledDescriptorsChanged) {
      try {
        if (
          this.props.scheduledColumns &&
          this.props.scheduledColumns.length > 0
        ) {
          sessionStorage.setItem(
            SS_SCHEDULED_COLUMN_DESCRIPTORS_KEY,
            JSON.stringify(this.props.scheduledColumns),
          );
          sessionStorage.setItem(
            SS_SCHEDULED_COLUMN_DESCRIPTORS_VERSION_KEY,
            String(INSPECT_DESCRIPTOR_SET_VERSION),
          );
        } else {
          sessionStorage.removeItem(SS_SCHEDULED_COLUMN_DESCRIPTORS_KEY);
          sessionStorage.removeItem(SS_SCHEDULED_COLUMN_DESCRIPTORS_VERSION_KEY);
        }
      } catch (e) {
        /* sessionStorage unavailable */
      }
    }

    this._maybeFetchHistory();
    this._maybeFetchScheduled();
    this._maybeFetchPerformance();
  }

  componentWillUnmount() {
    // Intentionally do not clear records on unmount. Records persist across
    // tab navigation so returning to /inspect shows data immediately without
    // a refetch. Account switch / logout reset inspect state via CLEAR_SLATE_VIEWS.
    //
    // A pending post-write reindex refresh (scheduleInspectReindexRefresh) is
    // a raw module-level setTimeout with no unmount hook of its own -- cancel
    // it so it does not dispatch fetches for a component that is gone.
    this.props.cancelInspectReindexRefresh();
  }

  _tryRestoreUrlSelection = function () {
    if (this._urlSelectionApplied) return false;
    this._urlSelectionApplied = true;
    var urlSelection = parseSelectionFromMatch(this.props.match, route);
    if (!urlSelection) {
      // No URL param -- if an inspection is already selected in state (a
      // previous selection restored from Redux), sync the URL to it.
      if (this.props.currentInspection) {
        var accountUUID =
          this.props.currentAccount && this.props.currentAccount.uuid;
        this.props.history.replace(
          buildSelectionPath(
            accountUUID,
            'inspection',
            this.props.currentInspection.uuid,
          ),
        );
        return false;
      }
      // Nothing selected and only one inspection exists: the dropdown hides
      // the "All Inspections" row below two, so land on that inspection.
      var only = soleInspectionUUID(
        this.props.inspectionsByInspection,
        this.props.inspectionsByAssetType,
      );
      if (only) {
        this.handleSelectionChange('inspection', only);
        return true;
      }
      return false;
    }
    // Validate against the loaded pivots
    var byInspection = this.props.inspectionsByInspection;
    var byAssetType = this.props.inspectionsByAssetType;
    if (urlSelection.mode === 'inspection') {
      if (!hasInspectionUUID(byInspection, byAssetType, urlSelection.value))
        return false;
    }
    if (urlSelection.mode === 'inspectionType') {
      var typeValue = urlSelection.value || {};
      if (
        !hasInspectionType(
          byInspection,
          byAssetType,
          typeValue.inspectionUUID,
          typeValue.assetTypeValue,
        )
      )
        return false;
    }
    if (urlSelection.mode === 'assetType') {
      var hash = urlSelection.value && urlSelection.value.hash;
      var value = urlSelection.value && urlSelection.value.value;
      if (!hasAssetTypeNode(byAssetType, hash, value)) return false;
    }
    // Re-dispatching the identical selection would null loaded data (e.g.
    // performance) without changing any fetch key, so _maybeFetch* would skip
    // the refetch and the tab would skeleton forever. Mirrors the
    // isSameSelection guard in handleSelectionChange.
    if (
      isSameSelection(
        urlSelection.mode,
        urlSelection.value,
        this.props.selectionMode,
        this.props.selectionValue,
      )
    ) {
      return false;
    }
    this.props.setInspectSelection(urlSelection.mode, urlSelection.value);
    return true;
  }.bind(this);

  _maybeFetchHistory = function () {
    if (this.state.activeTab !== TABS.HISTORY) return;
    var key = historyFetchKey(this.props);
    if (!key) return;
    if (key === this._lastFetchedHistoryKey) return;
    this._lastFetchedHistoryKey = key;
    this._historyLoadMore = false;
    this.props.fetchInspectionHistory({ isNewSet: true });
  }.bind(this);

  _maybeFetchScheduled = function () {
    if (this.state.activeTab !== TABS.SCHEDULED) return;
    var key = scheduledFetchKey(this.props);
    if (!key) return;
    if (key === this._lastFetchedScheduledKey) return;
    this._lastFetchedScheduledKey = key;
    this._scheduledLoadMore = false;
    this.props.fetchInspectionScheduled({ isNewSet: true });
  }.bind(this);

  _maybeFetchPerformance = function () {
    if (this.state.activeTab !== TABS.PERFORMANCE) return;
    var key = performanceFetchKey(this.props);
    if (!key) return;
    if (key === this._lastFetchedPerformanceKey) return;
    this._lastFetchedPerformanceKey = key;
    this.props.fetchInspectionPerformance();
  }.bind(this);

  handleDropdownOpen = function (e) {
    if (e) e.preventDefault();
    this.setState({
      isDropdownOpen: true,
      dropdownAnchorEl: e.currentTarget,
    });
  }.bind(this);

  handleDropdownClose = function () {
    this.setState({
      isDropdownOpen: false,
      dropdownAnchorEl: null,
    });
  }.bind(this);

  handleSelectionChange = function (mode, value) {
    // Re-selecting the identical selection is a no-op. setInspectSelection would
    // clear the loaded records, but the fetch key is unchanged so _maybeFetch*
    // skips the refetch -- leaving the tab blank until a manual refresh. Keep the
    // existing data in place instead.
    if (
      isSameSelection(
        mode,
        value,
        this.props.selectionMode,
        this.props.selectionValue,
      )
    ) {
      this.handleDropdownClose();
      return;
    }
    this.props.setInspectSelection(mode, value);
    this.handleDropdownClose();
    // Update URL to persist selection across refreshes
    var accountUUID =
      this.props.currentAccount && this.props.currentAccount.uuid;
    var path = buildSelectionPath(accountUUID, mode, value);
    this.props.history.replace(path);
  }.bind(this);

  // The pivot is a view preference: swapping it re-groups the dropdown list
  // without changing the selection or triggering any fetch. Persisted to
  // sessionStorage so it survives navigation.
  handlePivotChange = function (pivot) {
    this.props.setInspectPivot(pivot);
    try {
      sessionStorage.setItem(SS_PIVOT_KEY, pivot);
    } catch (e) {
      /* sessionStorage unavailable */
    }
  }.bind(this);

  handleCreateClick = function () {
    var accountUUID =
      this.props.currentAccount && this.props.currentAccount.uuid;
    this.props.history.push(
      '/accounts/' + (accountUUID || '') + '/inspect/new',
    );
  }.bind(this);

  handleSettingsClick = function () {
    var currentInspection = this.props.currentInspection;
    if (currentInspection) {
      var accountUUID =
        this.props.currentAccount && this.props.currentAccount.uuid;
      this.props.history.push(
        '/accounts/' +
          (accountUUID || '') +
          '/inspect/' +
          currentInspection.uuid +
          '/configuration',
      );
    }
  }.bind(this);

  handleTabChange = function (tab) {
    try {
      sessionStorage.setItem(SS_ACTIVE_TAB_KEY, tab);
    } catch (e) {
      /* sessionStorage unavailable */
    }
    this.setState(
      { activeTab: tab },
      function () {
        this._maybeFetchHistory();
        this._maybeFetchScheduled();
        this._maybeFetchPerformance();
      }.bind(this),
    );
  }.bind(this);

  handleRefreshClick = function () {
    var activeTab = this.state.activeTab;
    // A manual refresh asks for fresh server data, and the underlying data
    // (completed inspections, due dates, metrics) is shared across tabs. So
    // mark every tab dirty by dropping all three fetch guards: the active tab
    // refetches immediately below, and each inactive tab reloads when it next
    // becomes active instead of showing a stale cache. Without this, the guard
    // keys (selection + filters/sort + date range) are unchanged by a refresh,
    // so an inactive tab's _maybeFetch* would skip the refetch on navigation.
    this._lastFetchedHistoryKey = null;
    this._lastFetchedScheduledKey = null;
    this._lastFetchedPerformanceKey = null;
    if (activeTab === TABS.HISTORY) {
      var key = historyFetchKey(this.props);
      if (key) {
        this._lastFetchedHistoryKey = key;
        this._historyLoadMore = false;
        this.props.fetchInspectionHistory({ isNewSet: true });
      }
    } else if (activeTab === TABS.SCHEDULED) {
      var scheduledKey = scheduledFetchKey(this.props);
      if (scheduledKey) {
        this._lastFetchedScheduledKey = scheduledKey;
        this._scheduledLoadMore = false;
        this.props.fetchInspectionScheduled({ isNewSet: true });
      }
    } else if (activeTab === TABS.PERFORMANCE) {
      var perfKey = performanceFetchKey(this.props);
      if (perfKey) {
        this._lastFetchedPerformanceKey = perfKey;
        this.props.fetchInspectionPerformance();
      }
    } else {
      this.props.clearInspectionRecords();
    }
  }.bind(this);

  // A Performance KPI card was clicked. The three schedule cards jump to the
  // Scheduled tab with a status-filter preset so each card shows exactly its
  // own rows: overdue/neglected use their raw status value, while upcoming
  // spans two statuses (due-soon and scheduled) so it stores the
  // INSPECT_STATUS_UPCOMING sentinel, which buildFilterDTO expands to an OR
  // of the two Equals filters. For passed/failed/total cards, jump to History
  // with a result-filter preset. The shared time-range selector is global
  // state and is intentionally left untouched here.
  handlePerformanceCardClick = function (type) {
    if (type === 'overdue' || type === 'neglected' || type === 'upcoming') {
      this.props.clearInspectScheduledColumnFilters();
      this.props.setInspectScheduledColumnFilter(
        'status',
        type === 'upcoming' ? INSPECT_STATUS_UPCOMING : type,
      );
      this.handleTabChange(TABS.SCHEDULED);
      return;
    }
    this.props.clearInspectColumnFilters();
    if (type === 'passed') {
      // 'passed' counts include closed (corrected) deficiencies, so the
      // filter must include both results to match the KPI count.
      this.props.setInspectColumnFilter('result', PASSED_RESULT_VALUES);
    } else if (type === 'failed') {
      // 'failed' is open deficiencies only; History narrows Fail to exclude
      // closed ones, so a plain fail filter matches the KPI count.
      this.props.setInspectColumnFilter('result', 'fail');
    }
    this.handleTabChange(TABS.HISTORY);
  }.bind(this);

  // A histogram column click narrows the whole Inspect scope to that
  // inspection -- the same path as picking it in the dropdown, so the URL,
  // current inspection, and fetch keys all update identically.
  handlePerformanceInspectionClick = function (inspectionUUID) {
    this.handleSelectionChange('inspection', inspectionUUID);
  }.bind(this);

  handleDateRangeChange = function (next) {
    // Re-applying the identical range (e.g. re-clicking the already-active
    // preset) is a no-op. setInspectDateRange clears the loaded data to mark it
    // dirty, but the fetch key is unchanged so _maybeFetch* would skip the
    // refetch -- leaving the tab blank. Skip the dispatch entirely. Mirrors the
    // isSameSelection guard in handleSelectionChange.
    if (isSameDateRange(next, this.props.dateRange)) return;
    this.props.setInspectDateRange(next);
  }.bind(this);

  handleColumnFilterChange = function (columnKey, value) {
    this.props.setInspectColumnFilter(columnKey, value);
  }.bind(this);

  handleColumnSortChange = function (columnKey, direction) {
    this.props.setInspectSort(columnKey, direction);
  }.bind(this);

  handleScheduledColumnFilterChange = function (columnKey, value) {
    this.props.setInspectScheduledColumnFilter(columnKey, value);
  }.bind(this);

  handleScheduledColumnSortChange = function (columnKey, direction) {
    this.props.setInspectScheduledSort(columnKey, direction);
  }.bind(this);

  // Clears one column filter from the tab bar's chip row. Chips render only
  // for the active tab, so the active tab picks which filter store to hit.
  handleFilterChipClear = function (columnKey) {
    if (this.state.activeTab === TABS.SCHEDULED) {
      this.props.setInspectScheduledColumnFilter(columnKey, '');
    } else {
      this.props.setInspectColumnFilter(columnKey, '');
    }
  }.bind(this);

  // History rows carry their own inspection UUID (the `inspection` column),
  // which wins over the pinned currentInspection so aggregate views ('all' /
  // asset type) still resolve. currentInspection is the fallback for a row
  // whose column is missing it. Shared by handleDeficiencyAction and
  // handleViewActivity so the one rule deciding which inspection a History
  // row belongs to lives in one place. Throws (rather than rejecting) so
  // each caller can turn it into its own Promise.reject with a plain
  // try/catch, matching this file's existing try/catch idiom.
  _resolveInspectionUUID = function (record) {
    var inspectionUUID =
      (record && record.inspection) ||
      (this.props.currentInspection && this.props.currentInspection.uuid);
    if (!record || !inspectionUUID) {
      throw new Error(
        this.props.t(
          'inspect:deficiencyPickInspection',
          'Open the inspection to change this record.',
        ),
      );
    }
    return inspectionUUID;
  }.bind(this);

  handleDeficiencyAction = function (record, action, note) {
    var inspectionUUID;
    try {
      inspectionUUID = this._resolveInspectionUUID(record);
    } catch (e) {
      return Promise.reject(e);
    }
    return this.props.postDeficiencyAction(
      inspectionUUID,
      record.slateUUID,
      action,
      note,
    );
  }.bind(this);

  // View Log (InspectRecords' row menu): resolves the inspectionUUID the
  // same way handleDeficiencyAction does, then fetches that entry's timeline
  // (spec section 7) -- the dialog no longer reads the dropped activityLog
  // column.
  handleViewActivity = function (record) {
    var inspectionUUID;
    try {
      inspectionUUID = this._resolveInspectionUUID(record);
    } catch (e) {
      return Promise.reject(e);
    }
    return this.props.fetchInspectionRecordActivity(
      inspectionUUID,
      record.slateUUID,
    );
  }.bind(this);

  // Void Inspection (InspectRecords' row menu): both calls resolve the
  // inspectionUUID the same way handleDeficiencyAction does.
  handleVoidPreview = function (record) {
    var inspectionUUID;
    try {
      inspectionUUID = this._resolveInspectionUUID(record);
    } catch (e) {
      return Promise.reject(e);
    }
    return this.props.fetchInspectionVoidPreview(
      inspectionUUID,
      record.slateUUID,
    );
  }.bind(this);

  handleVoidInspection = function (record, reason) {
    var inspectionUUID;
    try {
      inspectionUUID = this._resolveInspectionUUID(record);
    } catch (e) {
      return Promise.reject(e);
    }
    return this.props.postInspectionVoid(
      inspectionUUID,
      record.slateUUID,
      reason,
    );
  }.bind(this);

  openSlatePane = function (slateUUID) {
    var activeTab = this.state.activeTab;
    var sourceRecords =
      activeTab === TABS.SCHEDULED
        ? this.props.scheduledRecords
        : this.props.records;
    var navList = sourceRecords
      ? sourceRecords.map(function (r) {
          return r.slateUUID;
        })
      : [];
    // History rows are record entries; Scheduled rows are the assets.
    var slateType =
      activeTab === TABS.SCHEDULED ? slateTypes.slate : slateTypes.record;
    this.props.openSlateInPane(slateUUID, { navList: navList, slateType: slateType });
  }.bind(this);

  render() {
    var state = this.state;
    var props = this.props;
    var t = props.t;
    var currentInspection = props.currentInspection;
    var records = props.records;
    var inspectionsEntity = props.inspectionsEntity;
    var recordsEntity = props.recordsEntity;
    var slateInSlatePaneUUID = props.slateInSlatePaneUUID;

    var hasInspections = totalInspections(props) > 0;
    var isLoading =
      !inspectionsEntity || inspectionsEntity.isFetching !== false;
    var noInspections = !isLoading && !hasInspections;

    var isRecordsLoading = recordsEntity && recordsEntity.isFetching;
    var hasRecords = !!(records && records.length > 0);
    var hasMore = !!props.recordsToken;
    // Pagination (load-more) shows appended skeleton rows immediately; a full
    // reload (dirty or same-query refresh) keeps the current rows and is gated
    // through DelayedLoad below, so a fast reload never flashes the skeleton.
    var isHistoryPaginating =
      !!isRecordsLoading && hasRecords && this._historyLoadMore;
    var isHistoryReloading =
      !!isRecordsLoading && hasRecords && !this._historyLoadMore;
    var loadMore = function () {
      if (!hasMore) return;
      if (isRecordsLoading) return;
      this._historyLoadMore = true;
      props.fetchInspectionHistory({ isNewSet: false });
    }.bind(this);

    var scheduledEntity = props.scheduledEntity;
    var isScheduledLoading = scheduledEntity && scheduledEntity.isFetching;
    var scheduledRecords = props.scheduledRecords;
    var hasScheduled = !!(scheduledRecords && scheduledRecords.length > 0);
    var hasMoreScheduled = !!props.scheduledToken;
    var isScheduledPaginating =
      !!isScheduledLoading && hasScheduled && this._scheduledLoadMore;
    var isScheduledReloading =
      !!isScheduledLoading && hasScheduled && !this._scheduledLoadMore;
    var loadMoreScheduled = function () {
      if (!hasMoreScheduled) return;
      if (isScheduledLoading) return;
      this._scheduledLoadMore = true;
      props.fetchInspectionScheduled({ isNewSet: false });
    }.bind(this);

    // Performance has no pagination: any in-flight fetch with counts already on
    // screen is a reload, gated through DelayedLoad. A cold load (no counts yet)
    // is handled by InspectPerformance's own immediate skeleton.
    var performanceEntity = props.performanceEntity;
    var isPerformanceLoading =
      performanceEntity && performanceEntity.isFetching;
    var isPerformanceReloading = !!isPerformanceLoading && !!props.performance;

    // Chips on the right of the tab bar surface the active tab's column
    // filters. Only History and Scheduled have column filters.
    var filterChips = [];
    if (state.activeTab === TABS.HISTORY) {
      filterChips = buildFilterChips(
        props.columnFilters,
        props.recordsColumns,
        t,
      );
    } else if (state.activeTab === TABS.SCHEDULED) {
      filterChips = buildFilterChips(
        props.scheduledColumnFilters,
        props.scheduledColumns,
        t,
      );
    }

    return (
      <div className="inner-page inspect-page" tabIndex="0">
        <div className="inner-page-header">
          <div className="inner-page-title">
            {isLoading ? (
              <div className="inspect-selector-skeleton" aria-hidden="true" />
            ) : noInspections ? (
              <div className="inspect-selector-empty" aria-disabled="true">
                <span className="inspect-selector-empty-label">
                  {t('inspect:noInspectionsSelector')}
                </span>
                <i className="material-symbols-outlined inspect-selector-empty-chevron">
                  keyboard_arrow_down
                </i>
              </div>
            ) : (
              <RaisedButton
                label={
                  props.selectionMode === 'all'
                    ? 'All Inspections'
                    : props.selectionMode === 'assetType'
                      ? assetTypeDisplay(
                          assetTypeFieldNameForSelection(props),
                          props.selectionValue && props.selectionValue.value,
                        )
                      : props.selectionMode === 'inspectionType'
                        ? (currentInspection ? currentInspection.name : '') +
                          (props.selectionValue &&
                          props.selectionValue.assetTypeValue
                            ? ' · ' +
                              assetTypeDisplay(
                                assetTypeFieldNameForSelection(props),
                                props.selectionValue.assetTypeValue,
                              )
                            : '')
                        : currentInspection
                          ? currentInspection.name
                          : ''
                }
                labelPosition="before"
                onClick={this.handleDropdownOpen}
                backgroundColor={palette.sidebarBg}
                icon={
                  <i
                    className={
                      'material-symbols-outlined slate-view-selector-chevron' +
                      (state.isDropdownOpen
                        ? ' slate-view-selector-chevron-open'
                        : '')
                    }
                    style={{
                      color: palette.sidebarTextHover,
                      fontSize: 22,
                      position: 'absolute',
                      right: -4,
                      top: 7,
                    }}
                  >
                    keyboard_arrow_down
                  </i>
                }
                style={{
                  height: 36,
                  whiteSpace: 'nowrap',
                  borderRadius: 8,
                  boxShadow:
                    '0 2px 8px rgba(23, 42, 64, 0.3), 0 1px 3px rgba(0, 0, 0, 0.15)',
                }}
                buttonStyle={{
                  backgroundColor: palette.sidebarBgHover,
                  borderRadius: 8,
                  alignItems: 'center',
                  whiteSpace: 'nowrap',
                  width: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
                labelStyle={{
                  textTransform: 'none',
                  fontSize: 14,
                  fontWeight: 500,
                  color: palette.sidebarTextHover,
                  letterSpacing: '0.01em',
                  alignItems: 'center',
                  whiteSpace: 'nowrap',
                  width: 'calc(100% - 25px)',
                  maxWidth: 'calc(100% - 25px)',
                  marginRight: 25,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: 'block',
                }}
              />
            )}
          </div>

          <div className="inspect-actions">
            {!noInspections && (
              <InspectDateRange
                t={t}
                value={props.dateRange}
                onChange={this.handleDateRangeChange}
              />
            )}
            {!noInspections && (
              <Tooltip title={t('tooltips:refresh')} placement="top">
                <RaisedButton
                  icon={
                    <span
                      className="material-symbols-outlined inspect-action-refresh-icon"
                      style={{
                        color: palette.cardTitle,
                        fontSize: 22,
                        lineHeight: 1,
                      }}
                    >
                      rotate_auto
                    </span>
                  }
                  style={{ minWidth: 40, height: 36 }}
                  onClick={this.handleRefreshClick}
                />
              </Tooltip>
            )}
            {!noInspections && props.selectionMode === 'inspection' && (
              <div className="inspect-actions-divider" />
            )}
            {!noInspections && props.selectionMode === 'inspection' && (
              <Tooltip title={t('inspect:settingsTooltip')} placement="top">
                <RaisedButton
                  icon={
                    <span
                      className="material-symbols-outlined"
                      style={{
                        color: palette.cardTitle,
                        fontSize: 20,
                        lineHeight: 1,
                      }}
                    >
                      settings
                    </span>
                  }
                  style={{ minWidth: 40, height: 36 }}
                  onClick={this.handleSettingsClick}
                />
              </Tooltip>
            )}
          </div>
        </div>

        <div className="inspect-card">
          <div className="inspect-tabs">
            <button
              className={
                'inspect-tab' +
                (state.activeTab === TABS.PERFORMANCE ? ' active' : '')
              }
              onClick={function () {
                this.handleTabChange(TABS.PERFORMANCE);
              }.bind(this)}
              disabled={noInspections}
            >
              <span className="material-symbols-outlined inspect-tab-icon">
                monitoring
              </span>
              Performance
            </button>
            <button
              className={
                'inspect-tab' +
                (state.activeTab === TABS.HISTORY ? ' active' : '')
              }
              onClick={function () {
                this.handleTabChange(TABS.HISTORY);
              }.bind(this)}
              disabled={noInspections}
            >
              <span className="material-symbols-outlined inspect-tab-icon">
                history
              </span>
              History
            </button>
            <button
              className={
                'inspect-tab' +
                (state.activeTab === TABS.SCHEDULED ? ' active' : '')
              }
              onClick={function () {
                this.handleTabChange(TABS.SCHEDULED);
              }.bind(this)}
              disabled={noInspections}
            >
              <span className="material-symbols-outlined inspect-tab-icon">
                calendar_month
              </span>
              Scheduled
            </button>
            <button
              className={
                'inspect-tab' +
                (state.activeTab === TABS.REPORTS ? ' active' : '')
              }
              onClick={function () {
                this.handleTabChange(TABS.REPORTS);
              }.bind(this)}
              disabled={noInspections}
            >
              <span className="material-symbols-outlined inspect-tab-icon">
                bar_chart_4_bars
              </span>
              {t('inspect:tabReports')}
              <span className="inspect-tab-badge">
                {t('inspect:sectionComingSoon')}
              </span>
            </button>
            {filterChips.length > 0 && (
              <div className="inspect-tab-filters">
                {filterChips.map(
                  function (chip) {
                    return (
                      <span className="inspect-filter-chip" key={chip.key}>
                        <span className="inspect-filter-chip-text">
                          <span className="inspect-filter-chip-name">
                            {chip.label}
                          </span>
                          {': ' + chip.value}
                        </span>
                        <button
                          type="button"
                          className="inspect-filter-chip-clear"
                          aria-label={t('inspect:clearColumnFilter').replace(
                            '{name}',
                            chip.label,
                          )}
                          onClick={function () {
                            this.handleFilterChipClear(chip.key);
                          }.bind(this)}
                        >
                          <span className="material-symbols-outlined">
                            close
                          </span>
                        </button>
                      </span>
                    );
                  }.bind(this),
                )}
              </div>
            )}
          </div>

          <div className="inspect-container">
            {state.activeTab === TABS.HISTORY && (
              <DelayedLoad
                isLoading={isHistoryReloading}
                delay={700}
                minDuration={1000}
              >
                {function (showSkeleton) {
                  return (
                    <InspectRecords
                      records={records}
                      isLoading={isRecordsLoading}
                      onRowClick={this.openSlatePane}
                      slateInSlatePaneUUID={slateInSlatePaneUUID}
                      dateRange={historyDateBounds(props.dateRange)}
                      onCreateInspection={
                        noInspections ? this.handleCreateClick : null
                      }
                      emptyTitle={
                        noInspections
                          ? t('inspect:noInspectionsTitle')
                          : t('inspect:noRecordsTitle')
                      }
                      emptyDescription={
                        noInspections
                          ? t('inspect:noInspectionsDescription')
                          : t('inspect:noRecordsDescription')
                      }
                      emptyActionLabel={
                        noInspections ? t('inspect:createInspection') : null
                      }
                      hasMore={hasMore}
                      isLoadingMore={isHistoryPaginating}
                      isReloading={showSkeleton}
                      loadMore={loadMore}
                      columnFilters={props.columnFilters}
                      sortColumnKey={props.sortColumnKey}
                      sortDirection={props.sortDirection}
                      onFilterChange={this.handleColumnFilterChange}
                      onSortChange={this.handleColumnSortChange}
                      recordsColumns={props.recordsColumns}
                      customColumns={props.recordsCustomColumns}
                      selectionMode={props.selectionMode}
                      inspectionsByInspection={props.inspectionsByInspection}
                      onDeficiencyAction={this.handleDeficiencyAction}
                      onViewActivity={this.handleViewActivity}
                      onVoidPreview={this.handleVoidPreview}
                      onVoidInspection={this.handleVoidInspection}
                      imagesLinks={props.imagesLinks}
                    />
                  );
                }.bind(this)}
              </DelayedLoad>
            )}
            {state.activeTab === TABS.SCHEDULED && (
              <DelayedLoad
                isLoading={isScheduledReloading}
                delay={700}
                minDuration={1000}
              >
                {function (showSkeleton) {
                  return (
                    <InspectRecords
                      records={scheduledRecords}
                      isLoading={isScheduledLoading}
                      onRowClick={this.openSlatePane}
                      slateInSlatePaneUUID={slateInSlatePaneUUID}
                      dateRange={scheduledDateBounds(props.dateRange)}
                      onCreateInspection={
                        noInspections ? this.handleCreateClick : null
                      }
                      emptyTitle={
                        noInspections
                          ? t('inspect:noInspectionsTitle')
                          : t('inspect:scheduledEmptyTitle')
                      }
                      emptyDescription={
                        noInspections
                          ? t('inspect:noInspectionsDescription')
                          : t('inspect:scheduledEmptyDescription')
                      }
                      emptyActionLabel={
                        noInspections ? t('inspect:createInspection') : null
                      }
                      hasMore={hasMoreScheduled}
                      isLoadingMore={isScheduledPaginating}
                      isReloading={showSkeleton}
                      loadMore={loadMoreScheduled}
                      columnFilters={props.scheduledColumnFilters}
                      sortColumnKey={props.scheduledSortColumnKey}
                      sortDirection={props.scheduledSortDirection}
                      onFilterChange={this.handleScheduledColumnFilterChange}
                      onSortChange={this.handleScheduledColumnSortChange}
                      recordsColumns={props.scheduledColumns}
                      customColumns={props.scheduledCustomColumns}
                      selectionMode={props.selectionMode}
                      inspectionsByInspection={props.inspectionsByInspection}
                      showSummaryColumn={false}
                      dateOnlyTimestamps={true}
                    />
                  );
                }.bind(this)}
              </DelayedLoad>
            )}
            {state.activeTab === TABS.PERFORMANCE && (
              <DelayedLoad
                isLoading={isPerformanceReloading}
                delay={700}
                minDuration={1000}
              >
                {function (showSkeleton) {
                  return (
                    <InspectPerformance
                      performance={showSkeleton ? null : props.performance}
                      noInspections={noInspections}
                      onCardClick={this.handlePerformanceCardClick}
                      onInspectionClick={this.handlePerformanceInspectionClick}
                      onCreateInspection={
                        noInspections ? this.handleCreateClick : null
                      }
                      emptyTitle={
                        noInspections
                          ? t('inspect:noInspectionsTitle')
                          : t('inspect:noRecordsTitle')
                      }
                      emptyDescription={
                        noInspections
                          ? t('inspect:noInspectionsDescription')
                          : t('inspect:noRecordsDescription')
                      }
                      emptyActionLabel={
                        noInspections ? t('inspect:createInspection') : null
                      }
                      noDataTitle={t('inspect:performanceNoDataTitle')}
                      noDataDescription={t(
                        'inspect:performanceNoDataDescription',
                      )}
                      t={t}
                    />
                  );
                }.bind(this)}
              </DelayedLoad>
            )}
            {state.activeTab === TABS.REPORTS && (
              <EmptyState
                icon="bar_chart_4_bars"
                title={t('inspect:reportsEmptyTitle')}
                description={t('inspect:reportsEmptyDescription')}
              />
            )}
          </div>
        </div>

        <InspectDropdown
          open={state.isDropdownOpen}
          anchorEl={state.dropdownAnchorEl}
          onClose={this.handleDropdownClose}
          inspectionsByAssetType={props.inspectionsByAssetType}
          inspectionsByInspection={props.inspectionsByInspection}
          pivot={props.pivot}
          onPivotChange={this.handlePivotChange}
          selectionMode={props.selectionMode}
          selectionValue={props.selectionValue}
          onSelectionChange={this.handleSelectionChange}
          onCreateClick={this.handleCreateClick}
        />
      </div>
    );
  }
}

var mapStateToProps = function (state) {
  return {
    currentAccount: state.accounts.currentAccount,
    inspectionsByAssetType: state.inspect.inspectionsByAssetType,
    inspectionsByInspection: state.inspect.inspectionsByInspection,
    pivot: state.inspect.pivot,
    currentInspection: state.inspect.currentInspection,
    records: state.inspect.records,
    inspectionsEntity: state.entities[entityNames.GET_INSPECTIONS],
    recordsEntity: state.entities[entityNames.GET_INSPECTION_RECORDS],
    recordsToken: state.inspect.recordsToken,
    selectionMode: state.inspect.selectionMode,
    selectionValue: state.inspect.selectionValue,
    slateInSlatePaneUUID: state.slatePane.slateInSlatePaneUUID,
    columnFilters: state.inspect.columnFilters,
    dateRange: state.inspect.dateRange,
    sortColumnKey: state.inspect.sortColumnKey,
    sortDirection: state.inspect.sortDirection,
    recordsColumns: state.inspect.recordsColumns,
    recordsCustomColumns: state.inspect.recordsCustomColumns,
    scheduledRecords: state.inspect.scheduledRecords,
    scheduledColumns: state.inspect.scheduledColumns,
    scheduledCustomColumns: state.inspect.scheduledCustomColumns,
    scheduledToken: state.inspect.scheduledToken,
    scheduledColumnFilters: state.inspect.scheduledColumnFilters,
    scheduledSortColumnKey: state.inspect.scheduledSortColumnKey,
    scheduledSortDirection: state.inspect.scheduledSortDirection,
    scheduledEntity: state.entities[entityNames.GET_INSPECTION_SCHEDULED],
    performance: state.inspect.performance,
    performanceEntity: state.entities[entityNames.GET_INSPECTION_PERFORMANCE],
    imagesLinks: state.images.links,
  };
};

var mapDispatchToProps = {
  fetchInspections: fetchInspections,
  fetchInspectionHistory: fetchInspectionHistory,
  fetchInspectionScheduled: fetchInspectionScheduled,
  setInspectSelection: setInspectSelection,
  setInspectPivot: setInspectPivot,
  clearInspectionRecords: clearInspectionRecords,
  openSlateInPane: openSlateInPane,
  setInspectColumnFilter: setInspectColumnFilter,
  clearInspectColumnFilters: clearInspectColumnFilters,
  setInspectSort: setInspectSort,
  setInspectColumns: setInspectColumns,
  setInspectDateRange: setInspectDateRange,
  setInspectScheduledColumnFilter: setInspectScheduledColumnFilter,
  clearInspectScheduledColumnFilters: clearInspectScheduledColumnFilters,
  setInspectScheduledSort: setInspectScheduledSort,
  setInspectScheduledColumns: setInspectScheduledColumns,
  fetchInspectionPerformance: fetchInspectionPerformance,
  postDeficiencyAction: postDeficiencyAction,
  fetchInspectionRecordActivity: fetchInspectionRecordActivity,
  fetchInspectionVoidPreview: fetchInspectionVoidPreview,
  postInspectionVoid: postInspectionVoid,
  cancelInspectReindexRefresh: cancelInspectReindexRefresh,
};

export default withTranslation()(
  withRouter(connect(mapStateToProps, mapDispatchToProps)(Inspect)),
);
